import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { invoicesAPI } from '../lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input, Label } from '../components/ui/input';
import { ArrowLeft, Receipt, Calendar, Euro, Check, AlertCircle, ExternalLink, Trash2, Edit2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { formatDate } from '../lib/utils';

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Virement');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentComment, setPaymentComment] = useState('');
  const [paymentType, setPaymentType] = useState('full');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({ notes: '', period_start: '', period_end: '' });
  const [abbyLoading, setAbbyLoading] = useState(false);
  const [abbyError, setAbbyError] = useState('');
  const [abbySuccess, setAbbySuccess] = useState('');

  useEffect(() => {
    loadInvoice();
  }, [invoiceId]);

  const loadInvoice = async () => {
    try {
      const response = await invoicesAPI.detail(invoiceId);
      setInvoice(response.data);
      setPaymentAmount((response.data.amount_remaining ?? response.data.amount_total ?? 0).toString());

      // Charger l'historique des paiements
      const { data: paymentsData } = await supabase
        .from('invoice_payments')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('payment_date', { ascending: true });
      setPayments(paymentsData || []);
    } catch (error) {
      console.error('Error loading invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    setUpdating(true);
    try {
      const newPaymentAmount = paymentType === 'full'
        ? parseFloat(invoice.amount_remaining)
        : parseFloat(paymentAmount);

      // Insérer le paiement dans invoice_payments
      await supabase.from('invoice_payments').insert({
        invoice_id: invoiceId,
        amount: newPaymentAmount,
        payment_date: paymentDate,
        payment_method: paymentMethod,
        comment: paymentComment || null,
      });

      // Calculer le nouveau total payé
      const newTotalPaid = (invoice.amount_paid || 0) + newPaymentAmount;
      const newRemaining = Math.max(0, invoice.amount_total - newTotalPaid);
      const newStatus = newRemaining <= 0 ? 'payee' : 'partiellement_payee';

      // Mettre à jour la facture
      await supabase.from('invoices').update({
        amount_paid: newTotalPaid,
        amount_remaining: newRemaining,
        status: newStatus,
        payment_method: paymentMethod,
        payment_date: newStatus === 'payee' ? paymentDate : invoice.payment_date,
        last_partial_payment_date: paymentDate,
      }).eq('id', invoiceId);

      setPaymentComment('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      await loadInvoice();
      setShowPaymentModal(false);
    } catch (error) {
      console.error('Error updating invoice:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleEditInvoice = async () => {
    try {
      const appointments = (await supabase
        .from('appointments')
        .select('*')
        .eq('child_id', invoice.child_id)
        .gte('start_datetime', editData.period_start)
        .lte('start_datetime', editData.period_end + 'T23:59:59')
      ).data || [];

      const seances = appointments.filter(a => a.appointment_type === 'seance');
      const contract = (await supabase
        .from('contracts')
        .select('*')
        .eq('child_id', invoice.child_id)
        .eq('active', true)
        .single()
      ).data;

      let total = 0;
      if (contract.billing_mode === 'par_seance') {
        total = seances.reduce((sum, apt) => sum + contract.session_price + (apt.overrun_amount || 0), 0);
      } else {
        total = seances.reduce((sum, apt) => {
          const start = new Date(apt.start_datetime);
          const end = new Date(apt.end_datetime);
          const roundedMinutes = Math.round((end - start) / 60000 / 15) * 15;
          return sum + Math.round(contract.hourly_rate * (roundedMinutes / 60) * 100) / 100 + (apt.overrun_amount || 0);
        }, 0);
      }
      total = Math.round(total * 100) / 100;

      await supabase.from('invoices').update({
        notes: editData.notes || null,
        amount_total: total,
        amount_paid: 0,
        amount_remaining: total,
      }).eq('id', invoice.id);

      await supabase.from('invoice_lines').delete().eq('invoice_id', invoice.id);

      const lines = [];
      seances.forEach(apt => {
        const start = new Date(apt.start_datetime);
        const end = new Date(apt.end_datetime);
        const roundedMinutes = Math.round((end - start) / 60000 / 15) * 15;
        const aptDate = start.toISOString().split('T')[0];
        const sessionAmount = contract.billing_mode === 'par_seance'
          ? contract.session_price
          : Math.round(contract.hourly_rate * (roundedMinutes / 60) * 100) / 100;

        lines.push({
          invoice_id: invoice.id,
          appointment_id: apt.id,
          description: apt.title,
          date: aptDate,
          duration_minutes: contract.billing_mode === 'tarif_horaire' ? roundedMinutes : null,
          unit_price: contract.billing_mode === 'par_seance' ? contract.session_price : contract.hourly_rate,
          amount: sessionAmount,
          is_overrun: false,
        });

        if (apt.overrun_amount) {
          lines.push({
            invoice_id: invoice.id,
            appointment_id: apt.id,
            description: `Dépassement — ${apt.title}`,
            date: aptDate,
            duration_minutes: apt.overrun_minutes || null,
            unit_price: apt.overrun_amount,
            amount: apt.overrun_amount,
            is_overrun: true,
          });
        }
      });

      if (lines.length > 0) {
        await supabase.from('invoice_lines').insert(lines);
      }

      await loadInvoice();
      setShowEditModal(false);
    } catch (error) {
      console.error('Erreur modification facture:', error);
    }
  };

  const handleSendToAbby = async () => {
    setAbbyLoading(true);
    setAbbyError('');
    setAbbySuccess('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profile } = await supabase
        .from('professional_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (!profile?.abby_api_key) {
        setAbbyError('Clé API Abby manquante. Ajoutez-la dans votre profil → Intégrations.');
        setAbbyLoading(false);
        return;
      }

      const { data: child } = await supabase
        .from('children')
        .select('*')
        .eq('id', invoice.child_id)
        .single();

      const { data: parentLink } = await supabase
        .from('parent_child_links')
        .select('*, parents(*)')
        .eq('child_id', invoice.child_id)
        .maybeSingle();

      const parent = parentLink?.parents;

      const { data: contract } = await supabase
        .from('contracts')
        .select('*')
        .eq('child_id', invoice.child_id)
        .eq('active', true)
        .maybeSingle();

      const headers = {
        'Authorization': `Bearer ${profile.abby_api_key}`,
        'Content-Type': 'application/json',
      };

      const customerRes = await fetch('https://api.app-abby.com/v2/contacts', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          firstname: parent?.first_name || child.first_name,
          lastname: parent?.last_name || child.last_name,
          emails: parent?.email ? [parent.email] : [],
        }),
      });
      const customer = await customerRes.json();

      if (!customer?.id) {
        setAbbyError('Impossible de créer le client dans Abby.');
        return;
      }

      const invoiceRes = await fetch(`https://api.app-abby.com/v2/billing/invoice/${customer.id}`, {
        method: 'POST',
        headers,
      });
      const abbyInvoice = await invoiceRes.json();

      if (!abbyInvoice?.id) {
        setAbbyError('Impossible de créer la facture dans Abby.');
        return;
      }

      const designation = contract?.billing_mode === 'par_seance'
        ? `Séances - ${child.first_name} ${child.last_name}`
        : `Accompagnement - ${child.first_name} ${child.last_name}`;

      const unitPrice = contract?.billing_mode === 'par_seance'
        ? contract.session_price
        : (contract?.hourly_rate * contract?.session_duration_minutes / 60);

      await fetch(`https://api.app-abby.com/v2/billing/${abbyInvoice.id}/lines`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          lines: [{
            designation,
            unitPrice: unitPrice || invoice.amount_total,
            quantity: contract?.billing_mode === 'par_seance' ? contract.sessions_per_month : 1,
            vatCode: 'FR_0',
          }]
        }),
      });

      setAbbySuccess('Facture créée dans Abby ! Ouvrez Abby pour la finaliser.');
    } catch (error) {
      console.error('Erreur Abby:', error);
      setAbbyError("Erreur lors de l'envoi vers Abby. Vérifiez votre clé API.");
    } finally {
      setAbbyLoading(false);
    }
  };

  const handleSendInvoice = async () => {
    setUpdating(true);
    try {
      await invoicesAPI.updateStatus(invoiceId, { status: 'en_attente_paiement' });
      await loadInvoice();
    } catch (error) {
      console.error('Error sending invoice:', error);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusVariant = (status) => {
    const variants = {
      'payee': 'success',
      'en_attente_paiement': 'pending',
      'partiellement_payee': 'warning',
      'impayee': 'error',
      'brouillon': 'default'
    };
    return variants[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'payee': 'Payée',
      'en_attente_paiement': 'En attente de paiement',
      'partiellement_payee': 'Partiellement payée',
      'impayee': 'Impayée',
      'brouillon': 'Brouillon'
    };
    return labels[status] || status;
  };

  const getStatusDescription = (status) => {
    const descriptions = {
      'brouillon': "La facture est en brouillon et n'a pas encore été envoyée",
      'en_attente_paiement': 'La facture a été envoyée et est en attente de paiement',
      'partiellement_payee': 'Un paiement partiel a été reçu',
      'payee': 'La facture a été entièrement payée',
      'impayee': 'La facture est impayée (délai dépassé)'
    };
    return descriptions[status] || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-foreground-muted">Facture non trouvée</p>
      </div>
    );
  }

  const paymentMethods = ['Virement', 'Chèque', 'Espèces', 'Carte bancaire', 'Autre'];

  return (
    <div className="space-y-6 animate-in" data-testid="invoice-detail">
      <Link to="/invoices">
        <Button variant="ghost" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
      </Link>

      <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Receipt className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800 mb-2">{invoice.invoice_number}</h1>
                <Badge variant={getStatusVariant(invoice.status)} className="text-sm">
                  {getStatusLabel(invoice.status)}
                </Badge>
                <p className="text-sm text-foreground-muted mt-2">{getStatusDescription(invoice.status)}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-foreground-muted mb-1">Montant total</p>
              <p className="text-3xl font-bold text-slate-800">{invoice.amount_total.toFixed(2)}€</p>
              {invoice.amount_paid > 0 && (
                <p className="text-sm text-green-600 mt-1">Payé : {invoice.amount_paid.toFixed(2)}€</p>
              )}
              {invoice.amount_remaining > 0 && (
                <p className="text-sm text-red-600 mt-0.5">Reste : {invoice.amount_remaining.toFixed(2)}€</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Détails de la facture</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Date d'émission</p>
                <p className="text-sm text-slate-700">{formatDate(invoice.issue_date)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Montant total</p>
                <p className="text-sm text-slate-700">{invoice.amount_total.toFixed(2)}€</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Montant payé</p>
                <p className="text-sm font-semibold text-green-600">{(invoice.amount_paid || 0).toFixed(2)}€</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Montant restant</p>
                <p className={`text-sm font-semibold ${invoice.amount_remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {(invoice.amount_remaining ?? 0).toFixed(2)}€
                </p>
              </div>
              {invoice.notes && (
                <div className="md:col-span-2">
                  <p className="text-sm font-medium text-slate-600 mb-1">Notes</p>
                  <p className="text-sm text-slate-700">{invoice.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {invoice.invoice_lines && invoice.invoice_lines.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Détail des prestations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {invoice.invoice_lines
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map((line, i) => (
                    <div key={i} className={`flex justify-between items-center p-3 rounded-lg border text-sm ${
                      line.is_overrun ? 'bg-amber-50 border-amber-100' : 'bg-white border-slate-100'
                    }`}>
                      <div>
                        <p className="font-medium text-slate-700">
                          {line.is_overrun ? '⏱ ' : ''}{line.description}
                        </p>
                        <p className="text-xs text-foreground-muted mt-0.5">
                          {line.date && new Date(line.date).toLocaleDateString('fr-FR')}
                          {line.duration_minutes && ` — ${line.duration_minutes} min`}
                        </p>
                      </div>
                      <p className="font-semibold text-slate-800">{(line.amount ?? 0).toFixed(2)}€</p>
                    </div>
                  ))}
                <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                  <p className="font-semibold text-slate-700">Total</p>
                  <p className="font-bold text-primary text-lg">{(invoice.amount_total ?? 0).toFixed(2)}€</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Historique des paiements depuis invoice_payments */}
          {payments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Historique des paiements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {payments.map((payment, i) => (
                    <div key={payment.id} className={`flex items-center justify-between p-3 rounded-lg border ${
                      i === payments.length - 1 && invoice.status === 'payee'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-amber-50 border-amber-200'
                    }`}>
                      <div>
                        <p className="text-sm font-medium text-slate-700">
                          {i === payments.length - 1 && invoice.status === 'payee'
                            ? 'Paiement final'
                            : `Paiement partiel n°${i + 1}`}
                        </p>
                        <p className="text-xs text-foreground-muted">{formatDate(payment.payment_date)}</p>
                        {payment.payment_method && (
                          <p className="text-xs text-foreground-muted">Moyen : {payment.payment_method}</p>
                        )}
                        {payment.comment && (
                          <p className="text-xs text-slate-500 italic mt-0.5">"{payment.comment}"</p>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-slate-700">{payment.amount.toFixed(2)}€</p>
                    </div>
                  ))}
                  {payments.length > 1 && (
                    <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                      <p className="text-xs text-slate-500">Total encaissé</p>
                      <p className="text-sm font-bold text-green-600">
                        {payments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}€
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {invoice.status === 'brouillon' && (
                <Button variant="secondary" className="w-full"
                  onClick={() => setEditData({ notes: invoice.notes || '', period_start: '', period_end: '' }) || setShowEditModal(true)}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Modifier la facture
                </Button>
              )}
              {invoice.status === 'brouillon' && (
                <Button variant="danger" className="w-full"
                  onClick={async () => {
                    if (window.confirm('Supprimer cette facture brouillon ?')) {
                      await invoicesAPI.delete(invoice.id);
                      navigate('/invoices');
                    }
                  }}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer la facture
                </Button>
              )}
              {invoice.status === 'brouillon' && (
                <Button className="w-full" onClick={handleSendInvoice} disabled={updating}
                  data-testid="send-invoice-button">
                  <Check className="w-4 h-4 mr-2" />
                  Envoyer la facture
                </Button>
              )}
              {invoice.status !== 'payee' && invoice.status !== 'brouillon' && (
                <>
                  <Button className="w-full"
                    onClick={() => { setPaymentType('full'); setPaymentAmount(invoice.amount_remaining.toString()); setShowPaymentModal(true); }}
                    disabled={updating} data-testid="mark-paid-button">
                    <Check className="w-4 h-4 mr-2" />
                    Marquer comme payée
                  </Button>
                  <Button variant="secondary" className="w-full"
                    onClick={() => { setPaymentType('partial'); setPaymentAmount(''); setShowPaymentModal(true); }}
                    disabled={updating} data-testid="mark-partial-button">
                    <Euro className="w-4 h-4 mr-2" />
                    Paiement partiel
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="border-indigo-100 bg-indigo-50">
            <CardHeader>
              <CardTitle className="text-lg text-indigo-800">🔗 Envoyer vers Abby</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {abbyError && <p className="text-xs text-red-600 bg-red-50 p-2 rounded-lg">{abbyError}</p>}
              {abbySuccess && <p className="text-xs text-green-700 bg-green-50 p-2 rounded-lg">{abbySuccess}</p>}
              <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={handleSendToAbby} disabled={abbyLoading}>
                <ExternalLink className="w-4 h-4 mr-2" />
                {abbyLoading ? 'Envoi en cours...' : 'Envoyer vers Abby'}
              </Button>
              <p className="text-xs text-indigo-600">La facture sera créée en brouillon dans votre compte Abby.</p>
            </CardContent>
          </Card>

          <Card className="bg-background-subtle">
            <CardContent className="pt-6">
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    invoice.status === 'payee' ? 'bg-green-500' :
                    invoice.status === 'en_attente_paiement' ? 'bg-amber-500' :
                    invoice.status === 'impayee' ? 'bg-red-500' :
                    invoice.status === 'partiellement_payee' ? 'bg-orange-500' : 'bg-slate-400'
                  }`} />
                  <span className="text-foreground-muted">Statut : {getStatusLabel(invoice.status)}</span>
                </div>
                {invoice.amount_remaining > 0 && (
                  <p className="text-foreground-muted">Il reste {(invoice.amount_remaining ?? 0).toFixed(2)}€ à recevoir</p>
                )}
                {invoice.status === 'payee' && (
                  <p className="text-green-600 font-medium">✓ Facture entièrement payée</p>
                )}
                {invoice.status === 'impayee' && (
                  <div className="flex items-start gap-2 text-red-600">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p className="text-sm">Cette facture est impayée. Délai de paiement dépassé.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modale modifier */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader><CardTitle>Modifier la facture</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Début de période</Label>
                  <Input type="date" value={editData.period_start}
                    onChange={(e) => setEditData({...editData, period_start: e.target.value})} />
                </div>
                <div>
                  <Label>Fin de période</Label>
                  <Input type="date" value={editData.period_end}
                    onChange={(e) => setEditData({...editData, period_end: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <textarea value={editData.notes}
                  onChange={(e) => setEditData({...editData, notes: e.target.value})}
                  className="w-full bg-input border-transparent focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3 text-slate-700 outline-none min-h-[80px]"
                  placeholder="Notes sur la facture..." />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowEditModal(false)}>Annuler</Button>
                <Button className="flex-1" onClick={handleEditInvoice}
                  disabled={!editData.period_start || !editData.period_end}>
                  <Check className="w-4 h-4 mr-2" />Recalculer et sauvegarder
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modale paiement */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>{paymentType === 'full' ? 'Marquer comme payée' : 'Paiement partiel'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {paymentType === 'partial' && (
                <div>
                  <Label htmlFor="amount">Montant encaissé</Label>
                  <Input id="amount" type="number" step="0.01" value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="0.00" data-testid="payment-amount-input" />
                  <p className="text-xs text-foreground-muted mt-1">
                    Restant dû : {(invoice.amount_remaining ?? 0).toFixed(2)}€
                  </p>
                </div>
              )}
              {paymentType === 'full' && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-slate-700 mb-1">Montant à encaisser</p>
                  <p className="text-2xl font-bold text-slate-800">{(invoice.amount_remaining ?? 0).toFixed(2)}€</p>
                </div>
              )}
              <div>
                <Label htmlFor="payment-date">Date du paiement</Label>
                <Input id="payment-date" type="date" value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="payment-method">Moyen de paiement</Label>
                <select id="payment-method" value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full bg-input border-transparent focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3 text-slate-700 outline-none"
                  data-testid="payment-method-select">
                  {paymentMethods.map(method => <option key={method} value={method}>{method}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="payment-comment">Commentaire (optionnel)</Label>
                <input id="payment-comment" type="text" value={paymentComment}
                  onChange={(e) => setPaymentComment(e.target.value)}
                  placeholder="Ex: chèque n°1234, virement du 12/04..."
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1"
                  onClick={() => setShowPaymentModal(false)} disabled={updating}>
                  Annuler
                </Button>
                <Button className="flex-1" onClick={handleMarkAsPaid}
                  disabled={updating || (paymentType === 'partial' && (!paymentAmount || parseFloat(paymentAmount) <= 0))}
                  data-testid="confirm-payment-button">
                  {updating ? 'Enregistrement...' : 'Confirmer'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetail;